import { useId, useState } from "react";
import type { FormEvent } from "react";
import { demoApi, problemFieldErrors, problemMessage } from "@/api/client";
import type { PayrollEmployee } from "@/api/types";
import { DialogShell } from "./DialogShell";
import "./W4Dialog.css";

/**
 * ef-038-4 — the "Set W-4" dialog H2a.tsx opens from a roster row.
 *
 * A DIALOG COMPONENT, DELIBERATELY SEPARATE FROM ITS OWN CSS BUDGET. Extracted out of
 * H2a.tsx/H2a.css (fix, ef-038-4) once H2a.css crossed the 250-line page budget
 * `deploy/lib/css-budget.sh` gates on — that budget applies to `pages/*.css` only, and
 * component CSS living beside a component under `components/` was always the intended
 * escape valve (ADR 0009 §5: "the shell, tabs and table live in components/"). Nothing
 * about the dialog's markup, roles, or accessible names changed in the move — see
 * `people-w4.spec.ts`'s own locators, which this component still answers identically.
 *
 * SELF-CONTAINED ON PURPOSE. This owns its own form state, validation-error state, submit
 * handler, and focus trap — the caller only decides WHICH employee's election is being
 * edited (or that none is, i.e. the dialog is closed) and gets a single `onClose` to call
 * when this component is done, whether that is a Cancel/Escape/overlay-click or a
 * successful save. General-purpose in the same spirit as RowActions: this file knows
 * nothing about the H-2A roster specifically, only that it was handed one employee.
 */

interface W4FormState {
  filingStatus: string;
  multipleJobs: boolean;
  dependentsAmount: string;
  otherIncome: string;
  deductions: string;
  extraWithholding: string;
}

/** Seeds the form from the roster's OWN current values — never blank — so opening the
 *  dialog on an employee who already has an election on file shows it rather than a form
 *  that looks like nothing was ever saved. */
function formFromEmployee(employee: PayrollEmployee): W4FormState {
  return {
    filingStatus: employee.filingStatus,
    multipleJobs: employee.multipleJobs,
    dependentsAmount: String(employee.dependentsAmount),
    otherIncome: String(employee.otherIncome),
    deductions: String(employee.deductions),
    extraWithholding: String(employee.extraWithholding),
  };
}

export function W4Dialog({
  employee,
  onClose,
}: {
  employee: PayrollEmployee;
  /** Called on Cancel, Escape, an overlay click, AND after a successful save — the caller
   *  cannot tell those apart from this callback alone, and does not need to: in every case
   *  the dialog is done and should unmount. Focus-return to the row's own trigger is the
   *  caller's job (it is the one holding that ref), same division BoothReset/JobCodes use
   *  between "close the dialog" and "where focus goes next". */
  onClose: () => void;
}) {
  const [form, setForm] = useState<W4FormState>(() => formFromEmployee(employee));
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]> | null>(null);

  const filingStatusId = useId();
  const multipleJobsId = useId();
  const dependentsId = useId();
  const otherIncomeId = useId();
  const deductionsId = useId();
  const extraWithholdingId = useId();

  const formId = useId();

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFormError(null);
    setFieldErrors(null);
    setSaving(true);
    try {
      // Every field is sent on every save, not only the ones this dialog session touched
      // — the form was seeded from the roster's current values (formFromEmployee), so a
      // full write here is a no-op on anything the farmer did not change, and the API's
      // own partial-PATCH semantics (COALESCE in DemoRepository.UpdateEmployeeW4Async)
      // are what the direct-API tests exercise, not this dialog.
      await demoApi.updateEmployeeW4(employee.id, {
        filingStatus: form.filingStatus,
        multipleJobs: form.multipleJobs,
        dependentsAmount: Number(form.dependentsAmount),
        otherIncome: Number(form.otherIncome),
        deductions: Number(form.deductions),
        extraWithholding: Number(form.extraWithholding),
      });
      onClose();
    } catch (err) {
      setFieldErrors(problemFieldErrors(err));
      setFormError(problemMessage(err, "Could not save this W-4 election."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <DialogShell
      title={<>Set W-4 — {employee.name}</>}
      onClose={onClose}
      busy={saving}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <form id={formId} className="w4-dialog__form" onSubmit={(e) => void handleSubmit(e)} noValidate>
        <div className="w4-dialog__field">
          <label htmlFor={filingStatusId}>Filing status</label>
          <select
            id={filingStatusId}
            value={form.filingStatus}
            onChange={(e) => setForm({ ...form, filingStatus: e.target.value })}
            aria-invalid={fieldErrors?.filingStatus ? true : undefined}
          >
            <option value="single">Single</option>
            <option value="married_joint">Married filing jointly</option>
            <option value="head_of_household">Head of household</option>
          </select>
          {fieldErrors?.filingStatus && (
            <p className="w4-dialog__field-error" role="alert">
              {fieldErrors.filingStatus[0]}
            </p>
          )}
        </div>

        <div className="w4-dialog__field w4-dialog__field--checkbox">
          <input
            id={multipleJobsId}
            type="checkbox"
            checked={form.multipleJobs}
            onChange={(e) => setForm({ ...form, multipleJobs: e.target.checked })}
          />
          <label htmlFor={multipleJobsId}>Multiple jobs</label>
        </div>

        {(
          [
            ["dependentsAmount", dependentsId, "Dependents amount"],
            ["otherIncome", otherIncomeId, "Other income"],
            ["deductions", deductionsId, "Deductions"],
            ["extraWithholding", extraWithholdingId, "Extra withholding"],
          ] as const
        ).map(([field, id, fieldLabel]) => (
          <div className="w4-dialog__field" key={field}>
            <label htmlFor={id}>{fieldLabel}</label>
            <input
              id={id}
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={form[field]}
              onChange={(e) => setForm({ ...form, [field]: e.target.value })}
              aria-invalid={fieldErrors?.[field] ? true : undefined}
            />
            {fieldErrors?.[field] && (
              <p className="w4-dialog__field-error" role="alert">
                {fieldErrors[field][0]}
              </p>
            )}
          </div>
        ))}

        {formError && !fieldErrors && (
          <p className="w4-dialog__form-error" role="alert">
            {formError}
          </p>
        )}

      </form>
    </DialogShell>
  );
}
